import React, { createContext, useContext, useState, useEffect } from "react";
import { storage, STORAGE_KEYS } from "../utils/storage";

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

const lightTheme = {
  name: "light",
  isDark: false,
  primary: "#007AFF",
  secondary: "#BBBBBB",
  success: "#34C759",
  warning: "#FF9500",
  error: "#FF3B30",
  background: "#F2F2F7",
  surface: "#FFFFFF",
  text: "#000000",
  textSecondary: "#8E8E93",
  border: "#C6C6C8",
  placeholder: "#C7C7CD",
  shadow: "rgba(0, 0, 0, 0.1)",
};

const darkTheme = {
  name: "dark",
  isDark: true,
  primary: "#0A84FF",
  secondary: "#AAAAAA",
  success: "#30D158",
  warning: "#FF9F0A",
  error: "#FF453A",
  background: "#000000",
  surface: "#1C1C1E",
  text: "#FFFFFF",
  textSecondary: "#8E8E93",
  border: "#38383A",
  placeholder: "#48484A",
  shadow: "rgba(255, 255, 255, 0.1)",
};

export const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    loadTheme();
  }, []);

  const loadTheme = () => {
    try {
      const savedTheme = storage.getItem(STORAGE_KEYS.THEME);
      if (savedTheme !== null) {
        setIsDarkMode(savedTheme === "dark");
      } else {
        // Определяем системную тему
        const prefersDark = window.matchMedia(
          "(prefers-color-scheme: dark)",
        ).matches;
        setIsDarkMode(prefersDark);
      }
    } catch (error) {
      console.error("Error loading theme:", error);
    }
  };

  const toggleTheme = () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      storage.setItem(STORAGE_KEYS.THEME, newTheme ? "dark" : "light");
    } catch (error) {
      console.error("Error saving theme:", error);
    }
  };

  const setTheme = (themeName) => {
    try {
      const newIsDark = themeName === "dark";
      setIsDarkMode(newIsDark);
      storage.setItem(STORAGE_KEYS.THEME, themeName);
    } catch (error) {
      console.error("Error setting theme:", error);
    }
  };

  const theme = isDarkMode ? darkTheme : lightTheme;

  const value = {
    theme,
    isDarkMode,
    toggleTheme,
    setTheme,
    lightTheme,
    darkTheme,
  };

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};
